import * as z from 'zod';
import type { ZodOpenApiPathsObject } from 'zod-openapi';
import { ReasonSchema } from './catalogue.js';
import { problemResponse } from './common.js';

export const CUSTOMER_TYPES = ['b2c', 'b2b'] as const;
export const CUSTOMER_STATUSES = ['pending_approval', 'active', 'rejected', 'blocked'] as const;
export const CustomerTypeSchema = z.enum(CUSTOMER_TYPES).meta({ id: 'CustomerType' });
export const CustomerStatusSchema = z.enum(CUSTOMER_STATUSES).meta({ id: 'CustomerStatus' });

const optionalText = (max: number) => z.string().max(max).nullable().optional();

export const CustomerSummarySchema = z
  .object({
    id: z.uuid(),
    type: CustomerTypeSchema,
    status: CustomerStatusSchema,
    fullName: z.string(),
    phone: z.string().nullable().meta({ description: 'E.164, e.g. +380501234567' }),
    email: z.string().nullable(),
    loyaltyEnabled: z.boolean(),
    companyName: z.string().nullable(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'CustomerSummary' });

export const CustomerPageSchema = z
  .object({ items: z.array(CustomerSummarySchema), total: z.number().int() })
  .meta({ id: 'CustomerPage' });

export const CompanySchema = z
  .object({
    legalName: z.string(),
    edrpou: z.string().nullable(),
    ipn: z.string().nullable(),
    vatPayer: z.boolean(),
    legalAddress: z.string().nullable(),
    iban: z.string().nullable(),
  })
  .meta({ id: 'Company' });

export const ContactSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    role: z.string().nullable(),
  })
  .meta({ id: 'Contact' });

export const AddressSchema = z
  .object({
    id: z.uuid(),
    npCityRef: z.string(),
    npCityName: z.string().nullable(),
    npWarehouseRef: z.string(),
    npWarehouseName: z.string().nullable(),
    recipientName: z.string(),
    recipientPhone: z.string(),
    isDefault: z.boolean(),
  })
  .meta({ id: 'Address' });

export const CustomerCardSchema = z
  .object({
    id: z.uuid(),
    type: CustomerTypeSchema,
    status: CustomerStatusSchema,
    fullName: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    loyaltyEnabled: z.boolean(),
    wooCustomerId: z.number().int().nullable(),
    keycrmBuyerId: z.number().int().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    company: CompanySchema.nullable(),
    contacts: z.array(ContactSchema),
    addresses: z.array(AddressSchema),
  })
  .meta({ id: 'CustomerCard' });

export const CustomerQuerySchema = z.object({
  search: z
    .string()
    .max(100)
    .optional()
    .meta({ description: 'Name, phone, email, company or EDRPOU' }),
  type: CustomerTypeSchema.optional(),
  status: CustomerStatusSchema.optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export const CreateCustomerSchema = z
  .object({
    type: CustomerTypeSchema,
    fullName: z.string().max(200),
    phone: optionalText(40),
    email: optionalText(254),
    loyaltyEnabled: z.boolean().optional(),
  })
  .meta({ id: 'CreateCustomer' });

export const StoreCustomerLinkSchema = z
  .object({
    wooCustomerId: z
      .number()
      .int()
      .positive()
      .nullable()
      .meta({ description: 'The logged-in Woo user; null for a guest checkout' }),
    fullName: z.string().max(200),
    phone: optionalText(40),
    email: optionalText(254),
  })
  .meta({ id: 'StoreCustomerLink' });

export const StoreCustomerSchema = z
  .object({
    customerId: z.uuid(),
    type: CustomerTypeSchema,
    status: CustomerStatusSchema,
    priceTier: z.enum(['retail', 'b2b']).nullable().meta({
      description:
        'Prices the store shows this customer: partner prices (customer-prices), retail, or none (blocked)',
    }),
    created: z.boolean(),
  })
  .meta({ id: 'StoreCustomer' });

export const UpdateCustomerSchema = z
  .object({
    type: CustomerTypeSchema.optional(),
    fullName: z.string().max(200).optional(),
    phone: optionalText(40),
    email: optionalText(254),
    loyaltyEnabled: z.boolean().optional(),
  })
  .meta({ id: 'UpdateCustomer' });

export const ChangeCustomerStatusSchema = z
  .object({ status: CustomerStatusSchema, reason: ReasonSchema })
  .meta({ id: 'ChangeCustomerStatus' });

export const CompanyInputSchema = z
  .object({
    legalName: z.string().max(300),
    edrpou: optionalText(20),
    ipn: optionalText(20),
    vatPayer: z.boolean().optional(),
    legalAddress: optionalText(500),
    iban: optionalText(40),
  })
  .meta({ id: 'CompanyInput' });

export const ContactInputSchema = z
  .object({
    name: z.string().max(200),
    phone: optionalText(40),
    email: optionalText(254),
    role: optionalText(100),
  })
  .meta({ id: 'ContactInput' });

export const AddressInputSchema = z
  .object({
    npCityRef: z.string().max(64),
    npCityName: optionalText(200),
    npWarehouseRef: z.string().max(64),
    npWarehouseName: optionalText(300),
    recipientName: z.string().max(200),
    recipientPhone: z.string().max(40),
    isDefault: z.boolean().optional(),
  })
  .meta({ id: 'AddressInput' });

export type StoreCustomerLinkRequest = z.infer<typeof StoreCustomerLinkSchema>;
export type StoreCustomerDto = z.infer<typeof StoreCustomerSchema>;
export type CustomerSummaryDto = z.infer<typeof CustomerSummarySchema>;
export type CustomerPageDto = z.infer<typeof CustomerPageSchema>;
export type CustomerCardDto = z.infer<typeof CustomerCardSchema>;
export type CustomerQuery = z.infer<typeof CustomerQuerySchema>;
export type CreateCustomerRequest = z.infer<typeof CreateCustomerSchema>;
export type UpdateCustomerRequest = z.infer<typeof UpdateCustomerSchema>;
export type ChangeCustomerStatusRequest = z.infer<typeof ChangeCustomerStatusSchema>;
export type CompanyInput = z.infer<typeof CompanyInputSchema>;
export type ContactInput = z.infer<typeof ContactInputSchema>;
export type AddressInput = z.infer<typeof AddressInputSchema>;

const json = <T extends z.ZodType>(schema: T) => ({ 'application/json': { schema } });
const body = <T extends z.ZodType>(schema: T) => ({ required: true, content: json(schema) });
const card = { description: 'The updated customer card', content: json(CustomerCardSchema) };
const errors = {
  '400': problemResponse('Invalid input'),
  '401': problemResponse('Not signed in'),
  '403': problemResponse('Missing permission'),
  '404': problemResponse('No such customer (or contact / address)'),
};
const id = { path: z.object({ id: z.uuid() }) };
const contactId = { path: z.object({ id: z.uuid(), contactId: z.uuid() }) };
const addressId = { path: z.object({ id: z.uuid(), addressId: z.uuid() }) };

export const customerPaths: ZodOpenApiPathsObject = {
  '/api/v1/customers': {
    get: {
      summary: 'Search customers',
      requestParams: { query: CustomerQuerySchema },
      responses: { '200': { description: 'OK', content: json(CustomerPageSchema) }, ...errors },
    },
    post: {
      summary: 'Create a customer (active; phone or email required, phone unique)',
      requestBody: body(CreateCustomerSchema),
      responses: { '201': card, ...errors, '409': problemResponse('Phone already used') },
    },
  },
  '/api/v1/customers/link': {
    post: {
      summary:
        'Store customer link (DeliStore plugin, signed): find the customer by Woo user, phone or email, or create a B2C customer',
      requestBody: body(StoreCustomerLinkSchema),
      responses: {
        '200': { description: 'Linked', content: json(StoreCustomerSchema) },
        '400': problemResponse('Invalid input'),
        '401': problemResponse('Not signed by the store'),
        '403': problemResponse('Missing permission'),
      },
    },
  },
  '/api/v1/customers/{id}': {
    get: {
      summary: 'Customer card (every view is written to the audit log)',
      requestParams: id,
      responses: { '200': { description: 'OK', content: json(CustomerCardSchema) }, ...errors },
    },
    patch: {
      summary: 'Edit name, contacts, type or Q-points participation',
      requestParams: id,
      requestBody: body(UpdateCustomerSchema),
      responses: {
        '200': card,
        ...errors,
        '409': problemResponse('Phone already used'),
        '422': problemResponse('Rule violated (e.g. B2B → B2C with company details)'),
      },
    },
  },
  '/api/v1/customers/{id}/status': {
    put: {
      summary:
        'Block / unblock; approve or reject a registration (needs b2b.approve). Reason required',
      requestParams: id,
      requestBody: body(ChangeCustomerStatusSchema),
      responses: { '200': card, ...errors, '422': problemResponse('Transition not allowed') },
    },
  },
  '/api/v1/customers/{id}/company': {
    put: {
      summary: 'Set company details (B2B only)',
      requestParams: id,
      requestBody: body(CompanyInputSchema),
      responses: { '200': card, ...errors, '422': problemResponse('Customer is not B2B') },
    },
    delete: {
      summary: 'Remove company details',
      requestParams: id,
      responses: { '200': card, ...errors },
    },
  },
  '/api/v1/customers/{id}/contacts': {
    post: {
      summary: 'Add a contact person',
      requestParams: id,
      requestBody: body(ContactInputSchema),
      responses: { '201': card, ...errors },
    },
  },
  '/api/v1/customers/{id}/contacts/{contactId}': {
    put: {
      summary: 'Replace a contact person',
      requestParams: contactId,
      requestBody: body(ContactInputSchema),
      responses: { '200': card, ...errors },
    },
    delete: {
      summary: 'Remove a contact person',
      requestParams: contactId,
      responses: { '200': card, ...errors },
    },
  },
  '/api/v1/customers/{id}/addresses': {
    post: {
      summary: 'Add a Nova Post delivery address (the first one becomes the default)',
      requestParams: id,
      requestBody: body(AddressInputSchema),
      responses: { '201': card, ...errors },
    },
  },
  '/api/v1/customers/{id}/addresses/{addressId}': {
    put: {
      summary: 'Replace a delivery address (isDefault: true makes it the default)',
      requestParams: addressId,
      requestBody: body(AddressInputSchema),
      responses: { '200': card, ...errors },
    },
    delete: {
      summary: 'Remove a delivery address (the oldest remaining becomes the default)',
      requestParams: addressId,
      responses: { '200': card, ...errors },
    },
  },
};
